import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../../contexts/AuthContext';
import { apiFetch } from '../../../api/client';
import { withUnidade } from '../../../utils/storageByUnit';
import { paJson, paDownloadUrl } from './paApi';
import PaRichTextEditor from './PaRichTextEditor';
import { PaDocumentosPanel, PaEncaminharPanel } from './PaDocsEmailPanels';

const STATUS_OPTS = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'recebido', label: 'Recebido' },
  { value: 'valido', label: 'Válido' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'na', label: 'N/A' },
];

export default function ArcaProcessoAdministrativoDetalhePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [data, setData] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [destSel, setDestSel] = useState([]);
  const [emailExt, setEmailExt] = useState('');
  const [parecerEdit, setParecerEdit] = useState(null);
  const [minutaEdit, setMinutaEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prestadores, setPrestadores] = useState([]);
  const [prestadorSel, setPrestadorSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const d = await paJson(`/api/arca/processo-administrativo/${id}`, unidadeSelecionada);
      setData(d);
      setDestSel(
        (d.destinatarios || [])
          .filter((x) => x.userId)
          .map((x) => ({
            id: x.userId,
            nome: x.userNome,
            email: x.userEmail,
          }))
      );
      setEmailExt(
        (d.destinatarios || [])
          .filter((x) => x.externalEmail)
          .map((x) => x.externalEmail)
          .join(', ')
      );
      const ativoParecer = (d.pareceres || []).find((p) => p.status === 'rascunho') || d.pareceres?.[0];
      setParecerEdit(ativoParecer || null);
      const ativaMinuta = (d.minutas || []).find((m) => m.status === 'rascunho') || d.minutas?.[0];
      setMinutaEdit(ativaMinuta || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(withUnidade('/api/usuarios', unidadeSelecionada));
        const json = await res.json();
        const list = Array.isArray(json) ? json : json.usuarios || json.items || [];
        if (!cancelled) setUsuarios(list);
      } catch {
        if (!cancelled) setUsuarios([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unidadeSelecionada]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(withUnidade('/api/prestadores', unidadeSelecionada));
        const json = await res.json().catch(() => []);
        if (!cancelled) setPrestadores(Array.isArray(json) ? json : []);
      } catch {
        if (!cancelled) setPrestadores([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unidadeSelecionada]);

  useEffect(() => {
    if (!data?.contratoVinculado && !data?.contratoEmpresa) {
      setPrestadorSel(null);
      return;
    }
    const found = (prestadores || []).find(
      (p) =>
        (data.contratoVinculado && p.numeroContrato === data.contratoVinculado) ||
        (data.contratoEmpresa &&
          !data.contratoVinculado &&
          String(p.empresa || '').toLowerCase() === String(data.contratoEmpresa).toLowerCase())
    );
    setPrestadorSel(found || null);
  }, [data?.contratoVinculado, data?.contratoEmpresa, prestadores]);

  const salvarDados = async () => {
    setBusy(true);
    try {
      await paJson(`/api/arca/processo-administrativo/${id}`, unidadeSelecionada, {
        method: 'PATCH',
        body: JSON.stringify({
          numeroProcesso: data.numeroProcesso,
          objetoContrato: data.objetoContrato,
          contratoVinculado: data.contratoVinculado,
          contratoEmpresa: data.contratoEmpresa,
        }),
      });
      setOk('Dados salvos.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const salvarDestinatarios = async () => {
    setBusy(true);
    try {
      const externos = String(emailExt || '')
        .split(/[,;\s]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .map((externalEmail) => ({ externalEmail }));
      const users = destSel.map((u) => ({
        userId: u.id,
        userNome: u.nome,
        userEmail: u.email,
      }));
      await paJson(`/api/arca/processo-administrativo/${id}/destinatarios`, unidadeSelecionada, {
        method: 'PUT',
        body: JSON.stringify({ destinatarios: [...users, ...externos] }),
      });
      setOk('Destinatários salvos.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const patchChecklist = async (itemId, status) => {
    try {
      await paJson(
        `/api/arca/processo-administrativo/${id}/checklist/${itemId}`,
        unidadeSelecionada,
        { method: 'PATCH', body: JSON.stringify({ status }) }
      );
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const criarParecer = async (tipo) => {
    setBusy(true);
    try {
      await paJson(`/api/arca/processo-administrativo/${id}/pareceres`, unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify({ tipo }),
      });
      setOk('Parecer criado a partir do modelo.');
      await load();
      setTab(5);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const salvarParecer = async (finalizar = false) => {
    if (!parecerEdit?.id) return;
    setBusy(true);
    try {
      await paJson(
        `/api/arca/processo-administrativo/${id}/pareceres/${parecerEdit.id}`,
        unidadeSelecionada,
        {
          method: 'PATCH',
          body: JSON.stringify({
            conteudoHtml: parecerEdit.conteudoHtml,
            para: parecerEdit.para,
            de: parecerEdit.de,
            assunto: parecerEdit.assunto,
            status: finalizar ? 'finalizado' : undefined,
            aiAssisted: parecerEdit.aiAssisted,
          }),
        }
      );
      setOk(finalizar ? 'Parecer finalizado.' : 'Parecer salvo.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const melhorarParecerIa = async () => {
    if (!parecerEdit?.id) return;
    setBusy(true);
    try {
      const r = await paJson(
        `/api/arca/processo-administrativo/${id}/pareceres/${parecerEdit.id}/melhorar-ia`,
        unidadeSelecionada,
        { method: 'POST', body: JSON.stringify({ textoAtual: parecerEdit.conteudoHtml }) }
      );
      setParecerEdit((prev) => ({
        ...prev,
        conteudoHtml: r.textoSugerido,
        aiAssisted: true,
      }));
      setOk('Texto melhorado pela IA — revise e salve.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const criarMinuta = async () => {
    setBusy(true);
    try {
      await paJson(`/api/arca/processo-administrativo/${id}/minuta`, unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setOk('Minuta criada.');
      await load();
      setTab(6);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const salvarMinuta = async (finalizar = false) => {
    if (!minutaEdit?.id) return;
    setBusy(true);
    try {
      await paJson(
        `/api/arca/processo-administrativo/${id}/minuta/${minutaEdit.id}`,
        unidadeSelecionada,
        {
          method: 'PATCH',
          body: JSON.stringify({
            titulo: minutaEdit.titulo,
            conteudoHtml: minutaEdit.conteudoHtml,
            status: finalizar ? 'finalizado' : undefined,
            aiAssisted: minutaEdit.aiAssisted,
          }),
        }
      );
      setOk(finalizar ? 'Minuta finalizada.' : 'Minuta salva.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const melhorarMinutaIa = async () => {
    if (!minutaEdit?.id) return;
    setBusy(true);
    try {
      const r = await paJson(
        `/api/arca/processo-administrativo/${id}/minuta/${minutaEdit.id}/melhorar-ia`,
        unidadeSelecionada,
        { method: 'POST', body: JSON.stringify({ textoAtual: minutaEdit.conteudoHtml }) }
      );
      setMinutaEdit((prev) => ({ ...prev, conteudoHtml: r.textoSugerido, aiAssisted: true }));
      setOk('Minuta melhorada pela IA — revise e salve.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const userOptions = useMemo(
    () =>
      (usuarios || []).map((u) => ({
        id: u.id,
        nome: u.nome || u.name || u.email,
        email: u.email,
        label: `${u.nome || u.email}${u.setor ? ` · ${u.setor}` : ''}`,
      })),
    [usuarios]
  );

  const prestadorOptions = useMemo(
    () =>
      (prestadores || [])
        .filter((p) => p && (p.empresa || p.numeroContrato))
        .map((p) => ({
          ...p,
          label: `${p.numeroContrato || ''} — ${p.empresa || ''}`.replace(/^ — | — $/g, ''),
        }))
        .sort((a, b) => String(a.empresa || '').localeCompare(String(b.empresa || ''), 'pt-BR')),
    [prestadores]
  );

  const aplicarPrestadorDados = (p) => {
    setPrestadorSel(p);
    if (!p) {
      setData((d) => ({ ...d, contratoVinculado: '', contratoEmpresa: '' }));
      return;
    }
    setData((d) => ({
      ...d,
      contratoVinculado: p.numeroContrato || '',
      contratoEmpresa: p.empresa || '',
      objetoContrato:
        !String(d.objetoContrato || '').trim() && (p.objeto || p.objetoContrato)
          ? String(p.objeto || p.objetoContrato)
          : d.objetoContrato,
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return <Alert severity="error">{error || 'Processo não encontrado'}</Alert>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/arca/pdf/processo-administrativo')} sx={{ mb: 1 }}>
        Lista
      </Button>

      <Typography variant="h5" fontWeight={700} color="primary.main">
        {data.numeroProcesso || `PA #${data.id}`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
        <Chip label={data.tipo === 'aditivo' ? 'Aditivo' : 'Contratação'} color="primary" size="small" />
        <Chip label={data.status} size="small" variant="outlined" />
        {data.contratoVinculado && (
          <Chip label={`${data.contratoVinculado} — ${data.contratoEmpresa || ''}`} size="small" />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setOk('')}>
          {ok}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab label="Dados" />
        <Tab label="Checklist" />
        <Tab label="Documentos" />
        <Tab label="Destinatários" />
        <Tab label="Encaminhar" />
        <Tab label="Pareceres" />
        <Tab label="Minuta" />
      </Tabs>

      {tab === 0 && (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}>
          <TextField
            label="Nº processo"
            fullWidth
            margin="dense"
            value={data.numeroProcesso || ''}
            onChange={(e) => setData({ ...data, numeroProcesso: e.target.value })}
          />
          <Autocomplete
            sx={{ mt: 1 }}
            options={prestadorOptions}
            value={prestadorSel}
            onChange={(_, v) => aplicarPrestadorDados(v)}
            getOptionLabel={(o) => o.label || `${o.numeroContrato || ''} — ${o.empresa || ''}`}
            isOptionEqualToValue={(a, b) =>
              String(a?.id || a?.numeroContrato) === String(b?.id || b?.numeroContrato)
            }
            filterOptions={(opts, { inputValue }) => {
              const q = String(inputValue || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/\p{M}/gu, '');
              if (!q) return opts;
              return opts.filter((o) => {
                const hay = `${o.empresa || ''} ${o.numeroContrato || ''}`
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/\p{M}/gu, '');
                return hay.includes(q);
              });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id || option.numeroContrato}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.empresa}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Contrato {option.numeroContrato}
                    {option.setor ? ` · ${option.setor}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Buscar prestador (empresa / contrato)"
                placeholder="Digite o nome da empresa ou nº do contrato"
                helperText="Lista dos prestadores do módulo de Contratos (unidade atual)"
              />
            )}
            noOptionsText="Nenhum prestador encontrado"
          />
          <TextField
            label="Objeto"
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            value={data.objetoContrato || ''}
            onChange={(e) => setData({ ...data, objetoContrato: e.target.value })}
          />
          <TextField
            label="Contrato vinculado"
            fullWidth
            margin="dense"
            value={data.contratoVinculado || ''}
            onChange={(e) => setData({ ...data, contratoVinculado: e.target.value })}
          />
          <TextField
            label="Empresa"
            fullWidth
            margin="dense"
            value={data.contratoEmpresa || ''}
            onChange={(e) => setData({ ...data, contratoEmpresa: e.target.value })}
          />
          <Button variant="contained" startIcon={<SaveIcon />} disabled={busy} onClick={salvarDados} sx={{ mt: 1 }}>
            Salvar dados
          </Button>
        </Paper>
      )}

      {tab === 1 && (
        <Box>
          {(data.checklist || []).map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              sx={{ p: 1.5, mb: 1, border: '1px solid rgba(25,118,210,0.1)', borderRadius: 2 }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography fontWeight={700} sx={{ minWidth: 36 }}>
                  {item.codigo}
                </Typography>
                <Typography sx={{ flex: 1 }}>{item.descricao}</Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={item.status || 'pendente'}
                    onChange={(e) => patchChecklist(item.id, e.target.value)}
                  >
                    {STATUS_OPTS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {tab === 2 && (
        <PaDocumentosPanel
          processoId={id}
          unidadeSelecionada={unidadeSelecionada}
          documentos={data.documentos || []}
          busy={busy}
          setBusy={setBusy}
          onChanged={load}
          onError={setError}
          onOk={setOk}
        />
      )}

      {tab === 3 && (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Busque usuários do sistema de Contratos e/ou informe e-mails externos.
          </Typography>
          <Autocomplete
            multiple
            options={userOptions}
            value={destSel.map((d) => {
              const found = userOptions.find((u) => u.id === d.id);
              return found || { ...d, label: d.nome || d.email };
            })}
            onChange={(_, v) => setDestSel(v)}
            getOptionLabel={(o) => o.label || o.nome || o.email || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} label="Usuários do sistema" placeholder="Digite nome ou e-mail" />
            )}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="E-mails externos (separados por vírgula)"
            value={emailExt}
            onChange={(e) => setEmailExt(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" disabled={busy} onClick={salvarDestinatarios}>
            Salvar destinatários
          </Button>
        </Paper>
      )}

      {tab === 4 && (
        <PaEncaminharPanel
          processoId={id}
          unidadeSelecionada={unidadeSelecionada}
          data={data}
          documentos={data.documentos || []}
          busy={busy}
          setBusy={setBusy}
          onChanged={load}
          onError={setError}
          onOk={setOk}
        />
      )}

      {tab === 5 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => criarParecer('tecnico_financeiro')}
            >
              + Parecer Técnico/Financeiro
            </Button>
            <Button variant="outlined" disabled={busy} onClick={() => criarParecer('diretoria')}>
              + Parecer Diretoria Executiva
            </Button>
          </Box>

          {(data.pareceres || []).length > 0 && (
            <FormControl size="small" sx={{ mb: 2, minWidth: 280 }}>
              <InputLabel>Selecionar parecer</InputLabel>
              <Select
                label="Selecionar parecer"
                value={parecerEdit?.id || ''}
                onChange={(e) => {
                  const p = data.pareceres.find((x) => x.id === e.target.value);
                  setParecerEdit(p || null);
                }}
              >
                {data.pareceres.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    #{p.id} · {p.tipo === 'diretoria' ? 'Diretoria' : 'Técnico/Financeiro'} · {p.status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {parecerEdit ? (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}>
              <Chip
                size="small"
                label={parecerEdit.tipo === 'diretoria' ? 'Diretoria → Contratos' : 'Técnico + Financeiro'}
                color="primary"
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                margin="dense"
                label="PARA"
                value={parecerEdit.para || ''}
                onChange={(e) => setParecerEdit({ ...parecerEdit, para: e.target.value })}
              />
              <TextField
                fullWidth
                margin="dense"
                label="DE"
                value={parecerEdit.de || ''}
                onChange={(e) => setParecerEdit({ ...parecerEdit, de: e.target.value })}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Assunto"
                value={parecerEdit.assunto || ''}
                onChange={(e) => setParecerEdit({ ...parecerEdit, assunto: e.target.value })}
              />
              <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
                Conteúdo do despacho
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Use a barra de ferramentas: fontes, tamanho, negrito, itálico, listas, alinhamento e cores.
              </Typography>
              <PaRichTextEditor
                value={parecerEdit.conteudoHtml || ''}
                onChange={(html) => setParecerEdit({ ...parecerEdit, conteudoHtml: html })}
                minHeight={360}
                placeholder="Redija o despacho…"
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  disabled={busy}
                  onClick={melhorarParecerIa}
                >
                  Melhorar com IA
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} disabled={busy} onClick={() => salvarParecer(false)}>
                  Salvar
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => salvarParecer(true)}>
                  Finalizar
                </Button>
                <Button
                  startIcon={<PictureAsPdfIcon />}
                  href={paDownloadUrl(
                    `/api/arca/processo-administrativo/${id}/pareceres/${parecerEdit.id}/pdf`,
                    unidadeSelecionada
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  Baixar PDF
                </Button>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Modelo baseado nos despachos FIS-FIN e Diretoria Executiva (editável por processo).
              </Typography>
            </Paper>
          ) : (
            <Alert severity="info">Crie um parecer a partir do modelo Técnico/Financeiro ou Diretoria.</Alert>
          )}
        </Box>
      )}

      {tab === 6 && (
        <Box>
          <Button variant="outlined" disabled={busy} onClick={criarMinuta} sx={{ mb: 2 }}>
            + Nova minuta
          </Button>

          {(data.minutas || []).length > 0 && (
            <FormControl size="small" sx={{ mb: 2, minWidth: 280, display: 'block' }}>
              <InputLabel>Selecionar minuta</InputLabel>
              <Select
                label="Selecionar minuta"
                value={minutaEdit?.id || ''}
                onChange={(e) => {
                  const m = data.minutas.find((x) => x.id === e.target.value);
                  setMinutaEdit(m || null);
                }}
              >
                {data.minutas.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    #{m.id} · {m.titulo} · {m.status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {minutaEdit ? (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}>
              <TextField
                fullWidth
                margin="dense"
                label="Título"
                value={minutaEdit.titulo || ''}
                onChange={(e) => setMinutaEdit({ ...minutaEdit, titulo: e.target.value })}
              />
              <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
                Conteúdo da minuta
              </Typography>
              <PaRichTextEditor
                value={minutaEdit.conteudoHtml || ''}
                onChange={(html) => setMinutaEdit({ ...minutaEdit, conteudoHtml: html })}
                minHeight={400}
                placeholder="Redija a minuta…"
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  disabled={busy}
                  onClick={melhorarMinutaIa}
                >
                  Melhorar com IA
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} disabled={busy} onClick={() => salvarMinuta(false)}>
                  Salvar
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => salvarMinuta(true)}>
                  Finalizar
                </Button>
                <Button
                  startIcon={<PictureAsPdfIcon />}
                  href={paDownloadUrl(
                    `/api/arca/processo-administrativo/${id}/minuta/${minutaEdit.id}/pdf`,
                    unidadeSelecionada
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  PDF
                </Button>
                <Button
                  startIcon={<DescriptionIcon />}
                  href={paDownloadUrl(
                    `/api/arca/processo-administrativo/${id}/minuta/${minutaEdit.id}/docx`,
                    unidadeSelecionada
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  Word
                </Button>
              </Box>
            </Paper>
          ) : (
            <Alert severity="info">Crie uma minuta para editar e exportar em PDF/Word.</Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
